import { Collapse } from "antd";
import { useTranslation } from "react-i18next";
import { PanelHeaderIconButton } from "../../../../../../../../Layout/Buttons";
import {
  IconAdd,
  IconEdit,
  IconModified,
} from "../../../../../../../../../config/icons";
import { useSubjectDetailsContext } from "../PolicySubjectDetailsContext";

import { SubjectPaymentMethodModal } from "../../../../../../../../Widgets/Subjects/Forms/SubjectPaymentMethodForm";
import SubjectPaymentMethodsList from "../../../../../../../../Widgets/Subjects/SubjectPaymentMethodsList";
import PolicySubjectAddressesForm from "./PolicySubjectAddressesForm";
import {
  Address,
  Contacts,
  PaymentMethod,
  PersonalData,
} from "../../../../../../../../../redux/apiSpecifications/apiSubjects";
import { useState } from "react";
import SubjectContactsForm, {
  SubjectContactsModal,
} from "../../../../../../../../Widgets/Subjects/Forms/SubjectContactsForm";
import CollapseStyled from "../../../../../../../../../style/CollapseStyled";
import SubjectPersonalDataForm, {
  SubjectPersonalDataModal,
} from "../../../../../../../../Widgets/Subjects/Forms/SubjectPersonalDataForm";
import { AddressModal } from "../../../../../../../../Widgets/AddressModal";

const PolicySubjectTabDetails = () => {
  const { t } = useTranslation();

  const {
    subject,
    onSaveSubjectPersonalDataPromise,
    onSaveSubjectContactsPromise,
    onSaveSubjectAddressPromise,
    onDeleteSubjectAddressPromise,
    onSaveSubjectPaymentMethodPromise,
    onDeleteSubjectPaymentMethodPromise,
  } = useSubjectDetailsContext();

  const [editPersonalData, setEditPersonalData] = useState<boolean>(false);

  const [subjectAddress, setSubjectAddress] = useState<Address | undefined>(
    undefined
  );

  const [subjectContacts, setSubjectContacts] = useState<Contacts | undefined>(
    undefined
  );

  const [subjectPaymentMethod, setSubjectPaymentMethod] = useState<
    PaymentMethod | undefined
  >(undefined);

  const handleOnConfirmSubjectPersonalData = (
    subjectPersonalData: PersonalData
  ) => {
    const subjectPersonalDataToSave = { ...subjectPersonalData };

    setEditPersonalData(false);

    onSaveSubjectPersonalDataPromise(subject?.id!, subjectPersonalDataToSave)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  const handleOnConfirmSubjectContacts = (subjectContacts: Contacts) => {
    const subjectContactsToSave = { ...subjectContacts };

    setSubjectContacts(undefined);

    onSaveSubjectContactsPromise(subject?.id!, subjectContactsToSave)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  const handleOnConfirmSubjectAddress = (address: Address) => {
    setSubjectAddress(undefined);

    onSaveSubjectAddressPromise(address)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  const handleOnDeleteSubjectAddress = (addressId: number) => {
    onDeleteSubjectAddressPromise(addressId)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  const handleOnConfirmSubjectPaymentMethod = (
    subjectPaymentMethod: PaymentMethod
  ) => {
    const subjectPaymentMethodToSave = { ...subjectPaymentMethod };

    setSubjectPaymentMethod(undefined);

    onSaveSubjectPaymentMethodPromise(subjectPaymentMethodToSave)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  const handleOnDeleteSubjectPaymentMethod = (
    subjectPaymentMethod: PaymentMethod
  ) => {
    onDeleteSubjectPaymentMethodPromise(subjectPaymentMethod.id!)
      .then((_) => console.info(_))
      .catch((_) => console.error(_));
  };

  return (
    <>
      <CollapseStyled
        defaultActiveKey={[
          "personal-data",
          "contacts",
          "addresses",
          "payments-methods",
        ]}
      >
        <Collapse.Panel
          key={"personal-data"}
          header={t("PANEL-HEADERS.subject-personal-data")}
          extra={
            <PanelHeaderIconButton
              icon={<IconModified />}
              tooltip={t("TOOLTIPS.edit-subject-personal-data")}
              onClick={(event) => {
                setEditPersonalData(true);

                event.stopPropagation();
              }}
            />
          }
        >
          <SubjectPersonalDataForm
            subjectId={subject?.id}
            subjectPersonalData={subject?.personalData!}
            readonly={true}
          />

          {/* <pre>{JSON.stringify(subject, null, 2)}</pre> */}
        </Collapse.Panel>

        <Collapse.Panel
          key={"contacts"}
          header={t("PANEL-HEADERS.subject-contacts")}
          extra={
            <PanelHeaderIconButton
              icon={<IconEdit />}
              tooltip={t("TOOLTIPS.edit-subject-contacts")}
              onClick={(event) => {
                setSubjectContacts(subject?.contacts);
                event.stopPropagation();
              }}
            />
          }
        >
          <SubjectContactsForm
            subjectContacts={subject?.contacts}
            disableForm={true}
          />

          {/* <pre>{JSON.stringify(subject?.contacts, null, 2)}</pre> */}
        </Collapse.Panel>

        <Collapse.Panel
          key={"addresses"}
          header={t("PANEL-HEADERS.subject-addresses")}
        >
          <PolicySubjectAddressesForm
            addresses={subject?.addresses}
            onEditAddress={(address) => {
              setSubjectAddress(address);
            }}
            onDeleteAddress={handleOnDeleteSubjectAddress}
          />
        </Collapse.Panel>

        <Collapse.Panel
          key={"payments-methods"}
          header={t("PANEL-HEADERS.subject-payments-methods")}
          extra={
            subject?.paymentMethods!.length === 0 && (
              <PanelHeaderIconButton
                icon={<IconAdd />}
                tooltip={t("TOOLTIPS.add-subject-payment-method")}
                onClick={(event) => {
                  setSubjectPaymentMethod({ type: "B" });
                  event.stopPropagation();
                }}
              />
            )
          }
        >
          <SubjectPaymentMethodsList
            paymentMethods={subject?.paymentMethods!}
            onSelectPaymentMethod={(subjectPaymentsListItem) =>
              setSubjectPaymentMethod(
                subject?.paymentMethods!.find(
                  (_) => _.id === subjectPaymentsListItem.id
                )
              )
            }
            onDeletePaymentMethod={handleOnDeleteSubjectPaymentMethod}
          />

          {/* <pre>{JSON.stringify(subject?.payments, null, 2)}</pre> */}
          {/* <pre>{JSON.stringify(subjectPaymentMethod, null, 2)}</pre> */}
        </Collapse.Panel>
      </CollapseStyled>

      <SubjectPersonalDataModal
        open={editPersonalData}
        subjectId={subject?.id}
        subjectPersonalData={subject?.personalData}
        onCancel={() => setEditPersonalData(false)}
        onConfirm={handleOnConfirmSubjectPersonalData}
      />

      <SubjectContactsModal
        open={subjectContacts !== undefined}
        subjectContacts={subjectContacts}
        onCancel={() => setSubjectContacts(undefined)}
        onConfirm={handleOnConfirmSubjectContacts}
      />

      <AddressModal
        open={subjectAddress !== undefined}
        address={subjectAddress}
        onCancel={() => setSubjectAddress(undefined)}
        onConfirm={handleOnConfirmSubjectAddress}
      />

      {/* <SubjectAddressModal
        open={subjectAddress !== undefined}
        address={subjectAddress}
        onCancel={() => setSubjectAddress(undefined)}
        onConfirm={handleOnConfirmSubjectAddress}
      /> */}

      <SubjectPaymentMethodModal
        open={subjectPaymentMethod !== undefined}
        subjectPaymentMethod={subjectPaymentMethod}
        onCancel={() => setSubjectPaymentMethod(undefined)}
        onConfirm={handleOnConfirmSubjectPaymentMethod}
      />
    </>
  );
};

export default PolicySubjectTabDetails;
